import { LocationDetailForm } from "@/features/location/detail/LocationDetailForm";
import { useModal } from "@/features/modal/ModalStackManager";
import { PartnerDetailForm } from "@/features/partner/form/detailForm";
import { DetailBadge } from "@/features/ui/detail/DetailBadge";
import { DetailContent } from "@/features/ui/detail/DetailContent";
import { DetailField } from "@/features/ui/detail/DetailField";
import { DetailHeader } from "@/features/ui/detail/DetailHeader";
import { DetailInformTitle } from "@/features/ui/detail/DetailInformTitle";
import { DetailLabel } from "@/features/ui/detail/DetailLabel";
import { DetailLink } from "@/features/ui/detail/DetailLink";
import { DetailLinkWrapper } from "@/features/ui/detail/DetailLinkWrapper";
import { DetailTitle } from "@/features/ui/detail/DetailTitle";
import { UsersDetailForm } from "@/features/users/components/form/UsersDetailForm";
import { getUserCodeByUser } from "@/utils/checkData";
import { purchaseOrderStatusColor, setPurchaseOrderStatus } from "@/utils/scmDataStatus";
import { Badge, Checkbox, Space } from "@mantine/core";
import { PurchaseOrdersGet200ResponseRowsInner } from "@sizlcorp/sizl-api-document/dist/models";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

const OrderDetail = Object.assign(DetailHeader, {
  Title: DetailTitle,
  Field: DetailField,
  Label: DetailLabel,
  Content: DetailContent,
  Link: DetailLink,
  LinkWrapper: DetailLinkWrapper,
  Badge: DetailBadge,
  CheckBox: Checkbox,
  Inform: DetailInformTitle,
});

export interface OrderDetailHeaderProps {
  data: PurchaseOrdersGet200ResponseRowsInner | undefined;
}

export const OrderDetailHeader = (params: OrderDetailHeaderProps) => {
  const { data } = params;
  const { t } = useTranslation();
  const userCode = getUserCodeByUser();
  const { openModal } = useModal();
  return (
    <>
      <OrderDetail>
        <OrderDetail.Title>
          {`${userCode === "FLOUR" ? `[${t("purchaseOrder.purchaseOrder")}]` : `[${t("order.order")}]`}`}{" "}
          {data?.code}
        </OrderDetail.Title>
        <Space h={8} />
        <OrderDetail.Field>
          <OrderDetail.Label>
            {`${userCode === "FLOUR" ? t("purchaseOrder.id") : t("order.id")}`}
          </OrderDetail.Label>
          <OrderDetail.Content>{data?.id}</OrderDetail.Content>
        </OrderDetail.Field>
        <OrderDetail.Field>
          <OrderDetail.Label>
            {`${userCode === "FLOUR" ? t("purchaseOrder.code") : t("order.code")}`}
          </OrderDetail.Label>
          <OrderDetail.Content>{data?.code}</OrderDetail.Content>
        </OrderDetail.Field>
        <OrderDetail.Field>
          <OrderDetail.Label>{t("partner.partner")}</OrderDetail.Label>
          <OrderDetail.Content>
            <OrderDetail.Link
              onClick={() =>
                openModal(
                  <PartnerDetailForm partnerCode={data?.partnerCode} />,
                  null,
                  t("common.detail", { item: t("partner.partner") }),
                )
              }
            >
              {data?.partnerCode}
            </OrderDetail.Link>
          </OrderDetail.Content>
        </OrderDetail.Field>
        <OrderDetail.Field>
          <OrderDetail.Label>{t("purchaseOrder.status")}</OrderDetail.Label>
          <OrderDetail.Content>
            <Badge
              variant="filled"
              color={
                purchaseOrderStatusColor[data?.status as keyof typeof purchaseOrderStatusColor]
              }
            >
              {t(setPurchaseOrderStatus(data?.status ?? ""))}
            </Badge>
          </OrderDetail.Content>
        </OrderDetail.Field>
        <OrderDetail.Field>
          <OrderDetail.Label>{t("order.manager")}</OrderDetail.Label>
          <OrderDetail.Content>
            <OrderDetail.Link
              onClick={() =>
                openModal(
                  <UsersDetailForm UserCode={data?.userCode} />,
                  null,
                  t("common.detail", { item: t("order.manager") }),
                )
              }
            >
              {data?.userCode}
            </OrderDetail.Link>
          </OrderDetail.Content>
        </OrderDetail.Field>
        <OrderDetail.Field>
          <OrderDetail.Label>{t("inventory.toLocation")}</OrderDetail.Label>
          <OrderDetail.Content>
            <OrderDetail.Link
              onClick={() =>
                openModal(
                  <LocationDetailForm locationCode={data?.targetLocationCode} />,
                  null,
                  t("common.detail", { item: t("location.location") }),
                )
              }
            >
              {data?.targetLocationName}
            </OrderDetail.Link>
          </OrderDetail.Content>
        </OrderDetail.Field>
        <OrderDetail.Field>
          <OrderDetail.Label>{t("routing.descript")}</OrderDetail.Label>
          <OrderDetail.Content>{data?.description}</OrderDetail.Content>
        </OrderDetail.Field>
        <OrderDetail.Field>
          <OrderDetail.Label>{t("order.orderDate")}</OrderDetail.Label>
          <OrderDetail.Content>{dayjs(data?.orderDate).format("YYYY-MM-DD")}</OrderDetail.Content>
        </OrderDetail.Field>
        <OrderDetail.Field>
          <OrderDetail.Label>{t("order.deliveryDate")}</OrderDetail.Label>
          <OrderDetail.Content>
            {dayjs(data?.deliveryDeadline).format("YYYY-MM-DD")}
          </OrderDetail.Content>
        </OrderDetail.Field>
        <OrderDetail.Field>
          <OrderDetail.Label>{t("order.createdAt")}</OrderDetail.Label>
          <OrderDetail.Content>{dayjs(data?.createdAt).format("YYYY-MM-DD")}</OrderDetail.Content>
        </OrderDetail.Field>
        <OrderDetail.Field>
          <OrderDetail.Label>{t("info.updateInformation")}</OrderDetail.Label>
          <OrderDetail.Content>
            {data?.updatedAt === null ? "" : dayjs(data?.updatedAt).format("YYYY-MM-DD")}
          </OrderDetail.Content>
        </OrderDetail.Field>
      </OrderDetail>
    </>
  );
};
